/**
 * Run time (DEC-195, UI_SDD §R.13): the time a scan ran with every wait for a person
 * taken out (Pause, the AI-unavailable decision, an extra-rounds approval). Paused time
 * is never shown. A scan before migration 036 has no run time: it reads "run time not
 * recorded", never completed minus started, which would count the pauses.
 */
import { durationLabel } from './format'

/** 47 -> "47s", 840 -> "14m", 3900 -> "1h 05m". The short form for lists and headers. */
export function shortDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  return `${h}h ${String(m % 60).padStart(2, '0')}m`
}

export interface RunTimeSource {
  run_seconds?: number | null
  run_time_is_partial?: boolean | null
  waiting_for_person?: boolean | null
}

/**
 * The run time to show now. The server computes a live scan's run time at the moment it
 * answers; between answers it ticks from `fetchedAt`, unless the scan is over or waiting
 * for a person. Null when nothing was recorded.
 */
export function currentRunSeconds(
  src: RunTimeSource | null | undefined,
  opts: { terminal: boolean; fetchedAt: number; now: number; paused?: boolean },
): number | null {
  if (!src || typeof src.run_seconds !== 'number') return null
  if (opts.terminal || src.waiting_for_person || opts.paused) return src.run_seconds
  return src.run_seconds + Math.max(0, (opts.now - opts.fetchedAt) / 1000)
}

/** "ran 14m", "ran at least 9m", "run time not recorded" (finished), for list rows. */
export function ranPhrase(src: RunTimeSource | null | undefined): string {
  if (!src || typeof src.run_seconds !== 'number') return 'run time not recorded'
  return `ran ${src.run_time_is_partial ? 'at least ' : ''}${shortDuration(src.run_seconds)}`
}

/** "Ran 14m" for report headers, or null when not recorded. */
export function ranHeader(src: RunTimeSource | null | undefined): string | null {
  if (!src || typeof src.run_seconds !== 'number') return null
  return `Ran ${src.run_time_is_partial ? 'at least ' : ''}${shortDuration(src.run_seconds)}`
}

/** The status bar's value: "6m 12s", "at least 9m 03s". */
export function runTimeValue(seconds: number | null, partial = false): string {
  if (seconds === null) return 'not recorded'
  return `${partial ? 'at least ' : ''}${durationLabel(seconds)}`
}
